import type { PGD } from "../types";

export const pgdErythromycinImpetigo: PGD = {
  id: "pgd-3e-eryth-impetigo",
  reference: "3e",
  version: "1.0",
  drugRef: "erythromycin",
  indication: "Widespread non-bullous impetigo — penicillin allergic AND pregnant (16+)",
  validFrom: "2024-01-31",
  expiryDate: "2027-01-30",
  frameworkType: "pharmacy_first",
  population: {
    ageRange: { min: 16, max: 999 },
    pregnancyAllowed: true,
    breastfeedingAllowed: true,
    notes: "Only used when pregnant and penicillin allergic. Widespread non-bullous impetigo only.",
  },
  duration: { days: 5 },
  doseBands: [
    {
      id: "eryth-adult",
      resolveBy: "single",
      dose: 500,
      frequency: "FOUR times a day",
      comment: "Single dose band — entire population is 16+ pregnant.",
      formulations: [
        {
          formulationId: "eryth-500-tab",
          instruction: "Take ONE 500mg tablet FOUR times a day for 5 days",
          quantity: { count: 20, label: "20 tablets" },
        },
        {
          formulationId: "eryth-250-tab",
          instruction: "Take TWO 250mg tablets FOUR times a day for 5 days",
          quantity: { count: 40, label: "40 tablets" },
        },
        {
          formulationId: "eryth-250-gr-tab",
          instruction: "Take TWO 250mg gastro-resistant tablets FOUR times a day for 5 days",
          quantity: { count: 40, label: "40 tablets" },
        },
        {
          formulationId: "eryth-500-5ml",
          instruction: "Take 5mL (500mg) FOUR times a day for 5 days",
          quantity: { bottles: 1, label: "1 × 100mL bottle" },
        },
        {
          formulationId: "eryth-250-5ml",
          instruction: "Take 10mL (500mg) FOUR times a day for 5 days",
          quantity: { bottles: 2, label: "2 × 100mL bottles" },
        },
        {
          formulationId: "eryth-125-5ml",
          instruction: "Take 20mL (500mg) FOUR times a day for 5 days",
          quantity: { bottles: 4, label: "4 × 100mL bottles" },
        },
      ],
    },
  ],
  preSupplyChecks: [
    { id: "ex-consent-refused", question: "Has the patient/carer refused consent?" },
    {
      id: "ex-unable-swallow",
      question:
        "Unable to absorb oral medications and/or unable to swallow oral dosage formulations?",
    },
    { id: "ex-macrolide-allergy", question: "Allergy to erythromycin or any macrolide?" },
    {
      id: "ex-long-term-macrolide",
      question: "Currently on long-term erythromycin or another macrolide antibiotic?",
      hint: "e.g. erythromycin for asplenia prophylaxis, azithromycin for COPD/bronchiectasis",
    },
    { id: "ex-failed-abx", question: "Failed previous oral antimicrobial treatment for this episode of impetigo?" },
    { id: "ex-keto", question: "Ketogenic diet?" },
    { id: "ex-myasthenia", question: "Known myasthenia gravis?" },
    {
      id: "ex-qt",
      question: "History of QT prolongation (congenital or acquired) or ventricular cardiac arrhythmia including torsades de pointes?",
      linkRefs: ["crediblemeds-qt", "sads-drugs-avoid"],
    },
    {
      id: "ex-qt-meds",
      question: "Taking any medicine known to cause QT prolongation?",
      linkRefs: ["crediblemeds-qt", "sads-drugs-avoid"],
    },
    { id: "ex-electrolyte", question: "Known electrolyte disturbances (hypokalaemia or hypomagnesaemia)?" },
    { id: "ex-ckd5", question: "Known CKD stage 5 (eGFR <15 mL/min/1.73m²)?" },
    { id: "ex-macrolide-jaundice", question: "Previous history of macrolide-associated jaundice or hepatic dysfunction?" },
    { id: "ex-any-liver", question: "Any known or suspected liver disease?" },
    { id: "ex-hepatotoxic-meds", question: "Taking any potentially hepatotoxic medicine?" },
    { id: "ex-heart-disease", question: "Known heart disease?", hint: "coronary artery disease, severe cardiac insufficiency, conduction disturbances, bradycardia <50 bpm" },
    { id: "ex-porphyria", question: "Known porphyria?" },
    { id: "ex-typhoid", question: "Oral typhoid vaccine within ±3 days?" },
    { id: "int-simvastatin", question: "Taking simvastatin?" },
    { id: "int-tolterodine", question: "Taking tolterodine?" },
    { id: "int-amisulpride", question: "Taking amisulpride?" },
    { id: "int-ergot", question: "Taking ergotamine or dihydroergotamine?" },
    { id: "int-cisapride-pimozide", question: "Taking astemizole, cisapride, mizolastine, domperidone, pimozide, or terfenadine?" },
    { id: "int-chloroquine", question: "Taking chloroquine or hydroxychloroquine?" },
    { id: "int-colchicine", question: "Taking colchicine?" },
    { id: "int-ivabradine", question: "Taking ivabradine?" },
    { id: "int-doac", question: "Taking any DOAC?", hint: "apixaban, dabigatran, edoxaban, rivaroxaban" },
    { id: "int-ccb", question: "Taking a calcium channel blocker?", hint: "amlodipine, diltiazem, felodipine, lercanidipine, nifedipine, verapamil" },
    { id: "int-digoxin", question: "Taking digoxin?" },
    { id: "int-hypokalaemia-meds", question: "Taking medicines causing hypokalaemia?", hint: "diuretics, corticosteroids, xanthines" },
    {
      id: "int-cyp-inducers",
      question: "Taking strong CYP inducers?",
      hint: "rifampicin, rifabutin, phenytoin, carbamazepine, phenobarbital, St John's wort",
      linkRefs: ["indiana-flockhart"],
    },
  ],
  cautions: [
    { id: "caution-breastfeeding", text: "Breastfeeding — monitor nursing infant for GI disturbances, oral candida, rashes, drowsiness, irritability, sweating, loss of appetite" },
    { id: "caution-excipients", text: "Check excipients: lactose, sucrose, fructose, sorbitol; aspartame in phenylketonuria" },
  ],
  counselling: [
    "Take just before or with food",
    "Tablets should be swallowed whole",
    "Gastro-resistant tablets must not be crushed or chewed",
    "Complete the full 5-day course even if symptoms improve",
  ],
  counsellingPoints: [
    { text: "Take just before or with food" },
    { text: "Tablets should be swallowed whole" },
    { text: "Gastro-resistant tablets must NOT be crushed or chewed" },
    { text: "Complete the full 5-day course even if symptoms improve" },
    { text: "Manage discomfort with appropriate OTC pain relief (paracetamol or ibuprofen where appropriate)" },
    { text: "Return to pharmacy if symptoms worsen rapidly at any time" },
    { text: "Return if no improvement after completing the course" },
    { text: "Seek urgent help if signs of sepsis develop — call 999 or attend A&E" },
  ],
  outcomeLinkRefs: ["bad-impetigo-leaflet", "nhs-impetigo"],
};
